// src/components/Settings.tsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { axiosInstance } from "../utils/axiosConfig";
import { useConsents } from "../hooks/useConsents";
import MFAEnrollment from "./MFAEnrollment";
import "../styles/Settings.css";

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // consentsLoading from hook for future use
  const { consents } = useConsents();
  const [loading, setLoading] = useState(true);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [showMFAEnrollment, setShowMFAEnrollment] = useState(false);
  const [localConsents, setLocalConsents] = useState(consents);

  useEffect(() => {
    const fetchConsents = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get("/consents/all");
        setLocalConsents(response.data);

        // Fetch MFA status
        const mfaResponse = await axiosInstance.get("/auth/mfa/status");
        setMfaEnabled(mfaResponse.data.mfaEnabled);
      } catch (err) {
        toast.error("Failed to load settings");
      } finally {
        setLoading(false);
      }
    };
    fetchConsents();
  }, [location.key]); // Re-run on navigation

  const handleToggle = async (type: string, value: boolean) => {
    try {
      // Convert snake_case (data_storage) to kebab-case (data-storage) for API endpoint
      const endpoint = type.replace(/_/g, '-');
      await axiosInstance.post(
        `/consents/${endpoint}`,
        { agreed: value }
      );
      setLocalConsents(prev => ({ ...prev, [type]: value }));
      toast.success(value ? "Permission granted" : "Permission revoked");
    } catch (err) {
      toast.error("Failed to update consent");
    }
  };

  const handleDisableMFA = async () => {
    try {
      const password = prompt("Enter your password to disable MFA:");
      if (!password) return;

      await axiosInstance.post("/auth/mfa/disable", { password });
      setMfaEnabled(false);
      toast.success("MFA has been disabled");
    } catch (err: any) {
      const error = err.response?.data?.error || "Failed to disable MFA";
      toast.error(error);
    }
  };

  const handleMFASuccess = () => {
    setMfaEnabled(true);
    setShowMFAEnrollment(false);
    toast.success("MFA has been enabled!");
  };

  if (loading) return <div className="hb-loading">⏳ Loading settings...</div>;

  return (
    <div className="hb-settings-container">
      <button
        onClick={() => navigate("/dashboard")}
        className="hb-btn hb-btn-secondary"
      >
        ← Back to Dashboard
      </button>

      {/* Coach Message */}
      <div className="hb-coach-message">
        <span className="hb-coach-icon">🔒</span>
        <p>Your privacy, your control. Grant only the permissions you're comfortable with.</p>
      </div>

      <div className="hb-settings-card">
        <h1 className="hb-settings-title">Settings</h1>

        {/* Security Section - MFA */}
        <div className="hb-settings-section">
          <h2 className="hb-settings-section-title">🔐 Two-Factor Authentication (MFA)</h2>
          <div className="hb-settings-option">
            <div className="hb-settings-option-content">
              <h3 className="hb-settings-option-title">
                {mfaEnabled
                  ? "✓ Two-factor authentication is enabled. Your account is more secure."
                  : "⚠️ Two-factor authentication is not enabled. Enable it to protect your account."}
              </h3>
              <p className="hb-settings-option-text">
                {mfaEnabled
                  ? "MFA adds an extra layer of security by requiring a code from your authenticator app."
                  : "Enable MFA to protect your account with an additional verification step."}
              </p>
            </div>
            <div className="hb-settings-action">
              {mfaEnabled ? (
                <button
                  onClick={handleDisableMFA}
                  className="hb-btn hb-btn-secondary"
                >
                  Disable MFA
                </button>
              ) : (
                <button
                  onClick={() => setShowMFAEnrollment(true)}
                  className="hb-btn hb-btn-primary"
                >
                  Enable MFA
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Privacy & Permissions Section */}
        <div className="hb-settings-section">
          <h2 className="hb-settings-section-title">🎯 Privacy & Permissions</h2>
          <p className="hb-settings-section-description">Manage what data Sumbitrbuddy can use to help you</p>

          {/* Data Storage Consent */}
          <div className="hb-settings-consent">
            <div className="hb-settings-consent-info">
              <h3 className="hb-settings-consent-title">💾 Save Your Data</h3>
              <p className="hb-settings-consent-text">Store your resumes, applications, and preferences</p>
              <p className="hb-settings-consent-required">Needed for: Resume Manager, Job Recommendations, Application Tracking</p>
            </div>
            <label className="hb-settings-toggle">
              <input
                type="checkbox"
                checked={localConsents?.data_storage || false}
                onChange={e => handleToggle("data_storage", e.target.checked)}
              />
              <span className="hb-toggle-label">
                {localConsents?.data_storage ? '✓ Granted' : 'Not Granted'}
              </span>
            </label>
          </div>

          {/* AI Review Consent */}
          <div className="hb-settings-consent">
            <div className="hb-settings-consent-info">
              <h3 className="hb-settings-consent-title">🤖 AI Insights</h3>
              <p className="hb-settings-consent-text">Analyze your resume and match jobs with AI</p>
              <p className="hb-settings-consent-required">Needed for: Job Recommendations, Resume Analysis, Clarity Review</p>
            </div>
            <label className="hb-settings-toggle">
              <input
                type="checkbox"
                checked={localConsents?.ai_review || false}
                onChange={e => handleToggle("ai_review", e.target.checked)}
              />
              <span className="hb-toggle-label">
                {localConsents?.ai_review ? '✓ Granted' : 'Not Granted'}
              </span>
            </label>
          </div>

          {/* Email Access Consent */}
          <div className="hb-settings-consent">
            <div className="hb-settings-consent-info">
              <h3 className="hb-settings-consent-title">📧 Email Integration</h3>
              <p className="hb-settings-consent-text">Automatically track job application status updates</p>
              <p className="hb-settings-consent-required">Needed for: Email Integration, Automatic Status Tracking</p>
              <p className="hb-settings-consent-security">🔒 Read-only access via OAuth 2.0 - we cannot send or modify emails</p>
            </div>
            <label className="hb-settings-toggle">
              <input
                type="checkbox"
                checked={localConsents?.email_access || false}
                onChange={e => handleToggle("email_access", e.target.checked)}
              />
              <span className="hb-toggle-label">
                {localConsents?.email_access ? '✓ Granted' : 'Not Granted'}
              </span>
            </label>
          </div>

          {/* Web Crawl Consent */}
          <div className="hb-settings-consent">
            <div className="hb-settings-consent-info">
              <h3 className="hb-settings-consent-title">🌐 Job Search</h3>
              <p className="hb-settings-consent-text">Search job boards to find opportunities for you</p>
              <p className="hb-settings-consent-required">Needed for: Job Recommendations, Market Analysis</p>
            </div>
            <label className="hb-settings-toggle">
              <input
                type="checkbox"
                checked={localConsents?.web_crawl || false}
                onChange={e => handleToggle("web_crawl", e.target.checked)}
              />
              <span className="hb-toggle-label">
                {localConsents?.web_crawl ? '✓ Granted' : 'Not Granted'}
              </span>
            </label>
          </div>
        </div>

        {/* Subscription & Billing Section */}
        <div className="hb-settings-section">
          <h2 className="hb-settings-section-title">💳 Subscription & Billing</h2>
          <div className="hb-settings-option">
            <div className="hb-settings-option-content">
              <h3 className="hb-settings-option-title">Manage Your Subscription</h3>
              <p className="hb-settings-option-text">
                View your current plan, upgrade or downgrade, manage billing, and view payment history.
              </p>
            </div>
            <div className="hb-settings-action">
              <button
                onClick={() => navigate("/subscription")}
                className="hb-btn hb-btn-primary"
              >
                Go to Billing →
              </button>
            </div>
          </div>
        </div>

        {/* Privacy Notice */}
        <div className="hb-settings-privacy-notice">
          <h3 className="hb-privacy-notice-title">🛡️ Your Privacy Matters</h3>
          <ul className="hb-privacy-notice-list">
            <li>✓ Change these settings anytime</li>
            <li>✓ Permissions only enable associated features</li>
            <li>✓ Your data is encrypted and never shared</li>
            <li>✓ Read our Privacy Policy for details</li>
          </ul>
        </div>

      </div>

      {/* MFA Enrollment Modal */}
      <MFAEnrollment
        isOpen={showMFAEnrollment}
        onClose={() => setShowMFAEnrollment(false)}
        onSuccess={handleMFASuccess}
      />
    </div>
  );
};

export default Settings;